import HeartRateDownIcon from '@streamlinehq/streamlinehq/img/manual/monitor-heart-rate-down.svg';
import HeartRateUpIcon from '@streamlinehq/streamlinehq/img/streamline-light/health-beauty/monitoring/monitor-heart-rate-up.svg';
import TentIcon from '@streamlinehq/streamlinehq/img/streamline-light/outdoors-recreation/camping/camping-tent-sleep.svg';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { dollarsToPhysicalComparisons } from '@upright/common/model/output/scores/physical-intensity';
import { PhysicalComparisonLabel } from '@upright/common/model/output/scores/physical-intensity/unit-prices';
import { Valence } from '@upright/common/model/types';
import { friendlyShortImpactTitle, friendlyValence } from '@upright/common/utils';
import {
  NumberType,
  formatNumberEfficiently,
  formatNumberInEfficiently,
} from '@upright/ui/common/generic/utilities/format-numbers-efficiently';
import { PlainIconFigureBox } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/figure-box';
import { useMoneyFiguresForSelection } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/monetary/monetary-figure-boxes';
import PillsIcon from '@upright/ui/icons/img/manual/contraceptive-pills-sperm-2.svg';
import CounselingIcon from '@upright/ui/icons/img/manual/counseling-1.svg';
import EngineerIcon from '@upright/ui/icons/img/manual/design-person-pencil.svg';
import DishwasherIcon from '@upright/ui/icons/img/manual/dishwasher.svg';
import FakeIcon from '@upright/ui/icons/img/manual/fake-photo.svg';
import WaterIcon from '@upright/ui/icons/img/manual/wave.svg';
import NerdIcon from '@upright/ui/icons/img/streamline-light/avatars/geometric-women-people/people-woman-graduate.svg';
import ScientistIcon from '@upright/ui/icons/img/streamline-light/avatars/geometric-women-professions/avatar-scientist-woman.svg';
import DoctorIcon from '@upright/ui/icons/img/streamline-light/avatars/geometric-women-professions/professions-woman-doctor-2.svg';
import JobsIcon from '@upright/ui/icons/img/streamline-light/avatars/natural-men-professions/professions-man-welder.svg';
import BuildingIcon from '@upright/ui/icons/img/streamline-light/building-construction/buildings/buildings.svg';
import HouseIcon from '@upright/ui/icons/img/streamline-light/building-construction/houses/house-1.svg';
import ShirtIcon from '@upright/ui/icons/img/streamline-light/clothes-footwear-accessories/shirts-sweaters-top/shirt-female.svg';
import LaptopIcon from '@upright/ui/icons/img/streamline-light/computers-devices-electronics/laptop-computers/laptop.svg';
import CutTreeIcon from '@upright/ui/icons/img/streamline-light/ecology/deforestation/deforestation-cut-tree.svg';
import SoilIcon from '@upright/ui/icons/img/streamline-light/ecology/plant-growth-organic-fertilizer/organic-plant-root.svg';
import Co2Icon from '@upright/ui/icons/img/streamline-light/ecology/pollution/pollution-co-2.svg';
import WaterPollutionIcon from '@upright/ui/icons/img/streamline-light/ecology/pollution/pollution-faucet-water.svg';
import PlasticBagIcon from '@upright/ui/icons/img/streamline-light/ecology/recycling/plastic-bag.svg';
import RollerCoasterIcon from '@upright/ui/icons/img/streamline-light/entertainment-events-hobbies/amusement-parks/amusement-park-rollercoaster.svg';
import GamblingIcon from '@upright/ui/icons/img/streamline-light/entertainment-events-hobbies/card-games/card-game-dice.svg';
import MealIcon from '@upright/ui/icons/img/streamline-light/food-drinks/asian-food/asian-food-noodle-ramen-bowl.svg';
import ChocolateBarIcon from '@upright/ui/icons/img/streamline-light/food-drinks/cakes-candies/chocolate-bar.svg';
import SpiritsIcon from '@upright/ui/icons/img/streamline-light/food-drinks/champagne-spirits/spirits-glass.svg';
import BananaIcon from '@upright/ui/icons/img/streamline-light/food-drinks/fruits/fruit-banana.svg';
import AsianInteriorIcon from '@upright/ui/icons/img/streamline-light/furnitures-decoration/asian-interiors/asian-interior-windows.svg';
import TranslateIcon from '@upright/ui/icons/img/streamline-light/interface-essential/text-options/translate.svg';
import TreeIcon from '@upright/ui/icons/img/streamline-light/nature-farming/trees/tree-2.svg';
import HippoIcon from '@upright/ui/icons/img/streamline-light/pets-animals/elephants-rhinoceros/hippo.svg';
import PhoneCallIcon from '@upright/ui/icons/img/streamline-light/phones-mobile-devices/phone-actions/phone-actions-smile.svg';
import LessonsIcon from '@upright/ui/icons/img/streamline-light/school-learning/school/school-board-chemistry.svg';
import SchoolIcon from '@upright/ui/icons/img/streamline-light/school-learning/school/school-teacher-maths.svg';
import MetalIcon from '@upright/ui/icons/img/streamline-light/tools-constructions/materials/metal-sheet.svg';
import RailRoadIcon from '@upright/ui/icons/img/streamline-light/transportation/railroad/railroad-fast-train.svg';
import RoadSign from '@upright/ui/icons/img/streamline-light/transportation/roads-lights/road-sign.svg';
import EvacuationCenterIcon from '@upright/ui/icons/img/streamline-light/travel/refugees-immigration/evacuation-center.svg';
import TVIcon from '@upright/ui/icons/img/streamline-light/users/geomertic-close-up-single-user-woman/single-woman-vintage-tv.svg';
import SmokingIcon from '@upright/ui/icons/img/streamline-light/wayfinding/allowances/allowances-smoking.svg';
import CareIcon from '@upright/ui/icons/img/streamline-light/wayfinding/disability/disability-cane.svg';
import DiamondIcon from '@upright/ui/icons/img/streamline-regular/money-payments-finance/wealth/diamond.svg';
import { compact, get } from 'lodash';
import { useNetImpactLensContext } from '../../../common/use-net-impact-lens-context';

export function useGenericProductComparisons() {
  const { dollarsPerYear, isFallback } = useMoneyFiguresForSelection();
  const context = useNetImpactLensContext();
  const [valence, impact] = [context?.state.valence, context?.state.impact];

  if (!valence || !impact || !dollarsPerYear) {
    return [];
  }
  if (impact === ImpactCategoryId.GHG_EMISSIONS) {
    return []; // Fix to leave out, this case is handled by special realLifeComparison hook
  }

  const physicalComparisons = dollarsToPhysicalComparisons(dollarsPerYear, impact, valence);

  // note: the logic of checking whether this is first is not solid as there can be null items in the array
  return compact(
    physicalComparisons.map(({ value, label, shortLabel }, index) => (
      <UnitBasedProductComparison
        value={value}
        label={label}
        shortLabel={shortLabel}
        impactCategoryId={impact}
        valence={valence}
        key={`generic-${label}`}
        first={index === 0}
        last={index === physicalComparisons.length - 1}
        isFallBackRevenue={isFallback}
      />
    )),
  );
}

function getExplanationText(
  formattedValue: string,
  impact: ImpactCategoryId,
  label: string,
  first: boolean,
  last: boolean,
  valence: Valence,
  isFallbackRevenue: boolean,
) {
  if (first) {
    return (
      <>
        {isFallbackRevenue ? 'Per $1M of revenue, the ' : 'The '}
        {friendlyValence(valence).toLowerCase()} <em>{friendlyShortImpactTitle(impact).toLowerCase()}</em> impact this
        company creates each year is equivalent in magnitude to <b>{formattedValue}</b> {label}
        {last ? '.' : '...'}
      </>
    );
  } else {
    return (
      <>
        ..or <b>{formattedValue}</b> {label}
        {last ? '.' : '...'}
      </>
    );
  }
}

type UnitBasedProductComparisonProps = {
  value: number;
  shortLabel: string | undefined;
  label: string;
  impactCategoryId: ImpactCategoryId;
  valence: Valence;
  first: boolean;
  last: boolean;
  isFallBackRevenue: boolean;
};

function UnitBasedProductComparison({
  value,
  label,
  shortLabel,
  impactCategoryId,
  valence,
  first,
  last,
  isFallBackRevenue,
}: UnitBasedProductComparisonProps) {
  const icon = getUnitIcon(label, valence);
  const formatter = getUnitFormatter(label, valence);
  const formattedValue = formatter ? formatter(value) : formatNumberEfficiently(value, NumberType.GENERIC, 3);
  return (
    <PlainIconFigureBox
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      icon={icon}
      numberText={formattedValue}
      textBelowNumber={shortLabel || label}
      explanationText={getExplanationText(
        formattedValue,
        impactCategoryId,
        label,
        first,
        last,
        valence,
        isFallBackRevenue,
      )}
    />
  );
}

function getUnitIcon(label: string, valence: Valence) {
  const iconEntry = get(unitIcons, label as PhysicalComparisonLabel, undefined);
  if (iconEntry && typeof iconEntry === 'object') {
    return iconEntry[valence];
  }
  return iconEntry;
}

export function getUnitFormatter(label: string, valence: Valence) {
  const entry = get(unitFormatters, label as PhysicalComparisonLabel, undefined);
  if (entry && typeof entry === 'object') {
    return entry[valence];
  }
  return entry;
}

type Icon = typeof JobsIcon;

// TODO: update units to have an enum and do lookup based on that,
// as this is super-fragile -- JO 19.4.2023

type FormatterFunction = (value: number) => string;
const unitFormatters: Partial<
  Record<PhysicalComparisonLabel, FormatterFunction | null | { [key in Valence]: FormatterFunction | null }>
> = {
  'cubic meters of spilled oil': (value: number) => formatNumberInEfficiently(value, 3),
  'cubic meters of remediated soil': (value: number) => formatNumberInEfficiently(value, 3),
  'cubic meters of treated wastewater': (value: number) => formatNumberInEfficiently(value, 3),
  'square meters of felled forest': (value: number) => formatNumberInEfficiently(value, 3),
  'highway kilometers': (value: number) => formatNumberInEfficiently(value, 3),
} as const;

const unitIcons: Partial<Record<PhysicalComparisonLabel, Icon | null | { [key in Valence]: Icon | null }>> = {
  'direct or indirect jobs': JobsIcon,
  'days of primary education': SchoolIcon,
  'person-years of primary health care': DoctorIcon,
  'long-haul train rides': RailRoadIcon,
  'five-storey apartment buildings': BuildingIcon,
  'days of elderly care': CareIcon,
  'half carat natural diamonds': DiamondIcon,
  'hours of interpretation services': TranslateIcon,
  'hours of cultural education': AsianInteriorIcon,
  'articles of fast fashion clothing': ShirtIcon,
  'packages of birth control pills': PillsIcon,
  'emergency shelter tents': EvacuationCenterIcon,
  'personal computers': LaptopIcon,
  'phase 3 trials of cardiovascular medicine': ScientistIcon,
  'hours of mechanical prototyping services': EngineerIcon,
  'online fake news articles that reach 5M readers': FakeIcon,
  'days of BBC television news programming': TVIcon,
  'science lessons': LessonsIcon,
  'person-years of scarce, highly educated workers': NerdIcon,
  'disability-adjusted life years (DALY)': {
    [Valence.NEGATIVE]: HeartRateDownIcon,
    [Valence.POSITIVE]: HeartRateUpIcon,
    [Valence.OTHER]: null,
  },
  'mosquito nets': TentIcon,
  cigarettes: SmokingIcon,
  'psychotherapy sessions': CounselingIcon,
  bananas: BananaIcon,
  'ready meals': MealIcon,
  'online gambling sessions': GamblingIcon,
  'bottles of vodka': SpiritsIcon,
  'phone calls': PhoneCallIcon,
  'hours of couples therapy': CounselingIcon,
  'hours of meaningless social media scrolling': null,
  'roller coaster rides': RollerCoasterIcon,
  'chocolate bars': ChocolateBarIcon,
  'cubic meters of spilled oil': WaterPollutionIcon,
  'cubic meters of remediated soil': SoilIcon,
  'dishwasher cycles': DishwasherIcon,
  'kilograms of germanium': MetalIcon,
  'cubic meters of treated wastewater': WaterIcon,
  'square meters of felled forest': CutTreeIcon,
  'highway kilometers': RoadSign,
  'square meters of planted forest': TreeIcon,
  'hours of biodiversity conservation services': HippoIcon,
  'plastic bags': PlasticBagIcon,
  'tons of GHG emissions': Co2Icon,
  'average annual emissions of U.S. households': HouseIcon,
} as const;
